// SettingsPanel — right-hand sidebar in ArticleEditPage.
//
// Two tabs:
//   • Settings — cover, status, schedule, tags, kind, premium, featured, author.
//   • Audit — last N audit_log entries scoped to this article (read-only).
//
// Cover upload uses the same "media_assets+" trick as inline media via the
// uploadCover handler the parent provides. Schedule is wired through
// parent props (so the StatusButton dropdown stays in sync).

import { useEffect, useMemo, useRef, useState } from "react";
import { list, fileUrl, createRecord } from "../../lib/pb";
import type {
  Article,
  ArticleKind,
  ArticleStatus,
  Editor,
  Tag,
  Series,
  AuditEntry,
} from "../../lib/types";
import { t as theme } from "./theme";
import { useT } from "../../lib/i18n";
import { statusLabel, kindLabel } from "../../pages/ArticlesPage";

export interface SettingsPanelProps {
  article: Article | null;
  editors: Editor[];
  selectedKind: ArticleKind;
  onKindChange: (k: ArticleKind) => void;
  status: ArticleStatus;
  onStatusChange: (s: ArticleStatus) => void;
  publishAt: string;
  onPublishAtChange: (v: string) => void;
  tags: string[];
  onTagsChange: (t: string[]) => void;
  isPremium: boolean;
  onPremiumChange: (v: boolean) => void;
  featured: boolean;
  onFeaturedChange: (v: boolean) => void;
  authorId: string;
  onAuthorChange: (id: string) => void;
  seriesId: string;
  onSeriesChange: (id: string) => void;
  seriesPosition: number;
  onSeriesPositionChange: (n: number) => void;
  pendingCover: File | null;
  onPendingCoverChange: (f: File | null) => void;
  onClose: () => void;
}

const KINDS: ArticleKind[] = ["video", "article", "visual_essay"];
const STATUSES: ArticleStatus[] = ["draft", "review", "scheduled", "published", "archived"];

export function SettingsPanel(props: SettingsPanelProps) {
  const t = useT();
  const [tab, setTab] = useState<"settings" | "audit">("settings");
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [allSeries, setAllSeries] = useState<Series[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const coverRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    list<Tag>("tags", { perPage: 200, sort: "name" })
      .then((r) => setAllTags(r.items))
      .catch(() => {
        /* tags optional */
      });
    list<Series>("series", { perPage: 200, sort: "-updated" })
      .then((r) => setAllSeries(r.items))
      .catch(() => {
        /* series optional */
      });
  }, []);

  useEffect(() => {
    if (tab !== "audit" || !props.article) return;
    setAuditLoading(true);
    list<AuditEntry>("audit_log", {
      perPage: 30,
      sort: "-created",
      filter: `target_collection = 'articles' && target_id = '${props.article.id}'`,
      expand: "actor",
    })
      .then((r) => setAudit(r.items))
      .catch(() => setAudit([]))
      .finally(() => setAuditLoading(false));
  }, [tab, props.article]);

  return (
    <aside
      style={{
        width: 320,
        flexShrink: 0,
        background: theme.card,
        borderLeft: `1px solid ${theme.border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        color: theme.text,
        fontFamily: theme.sans,
      }}
    >
      <div
        style={{
          display: "flex",
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        {(["settings", "audit"] as const).map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            style={{
              flex: 1,
              padding: "12px 14px",
              background: "transparent",
              border: "none",
              borderBottom: tab === tb ? `2px solid ${theme.accent}` : "2px solid transparent",
              color: tab === tb ? theme.text : theme.text3,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {tb === "settings" ? t.settingsPanelTabSettings : t.settingsPanelTabAudit}
          </button>
        ))}
        <button
          type="button"
          onClick={props.onClose}
          title={t.collapseSidebar}
          style={{
            padding: "0 12px",
            background: "transparent",
            border: "none",
            color: theme.text3,
            fontSize: 18,
            cursor: "pointer",
          }}
        >
          ›
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
        {tab === "settings" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Section label={t.settingsPanelCover}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(props.pendingCover || props.article?.cover) && (
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      borderRadius: 6,
                      border: `1px solid ${theme.border}`,
                      background: theme.bg,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.text3,
                      fontSize: 11,
                    }}
                  >
                    {props.pendingCover ? (
                      <span>{props.pendingCover.name}</span>
                    ) : props.article?.cover ? (
                      <img
                        src={fileUrl("articles", props.article.id, props.article.cover, "320x180")}
                        alt=""
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : null}
                  </div>
                )}
                <input
                  ref={coverRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    props.onPendingCoverChange(e.target.files?.[0] || null)
                  }
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => coverRef.current?.click()}
                    style={ghostBtn}
                  >
                    {props.article?.cover || props.pendingCover
                      ? t.settingsPanelReplaceCover
                      : t.settingsPanelUploadCover}
                  </button>
                  {props.pendingCover && (
                    <button
                      type="button"
                      onClick={() => props.onPendingCoverChange(null)}
                      style={ghostBtn}
                    >
                      {t.cancel}
                    </button>
                  )}
                </div>
              </div>
            </Section>

            <Section label={t.settingsPanelKind}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => props.onKindChange(k)}
                    style={pill(props.selectedKind === k)}
                  >
                    {kindLabel(t, k)}
                  </button>
                ))}
              </div>
            </Section>

            <Section label={t.settingsPanelStatus}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => props.onStatusChange(s)}
                    style={pill(props.status === s)}
                  >
                    {statusLabel(t, s)}
                  </button>
                ))}
              </div>
            </Section>

            <Section label={t.settingsPanelSchedule} hint={t.settingsPanelScheduleHint}>
              <input
                type="datetime-local"
                value={props.publishAt}
                onChange={(e) => props.onPublishAtChange(e.target.value)}
                style={input}
              />
            </Section>

            <Section label={t.settingsPanelTags}>
              <TagsInput
                allTags={allTags}
                selectedIds={props.tags}
                onChange={props.onTagsChange}
                onTagsListUpdate={setAllTags}
              />
            </Section>

            <Section label={t.settingsPanelAuthor}>
              <select
                value={props.authorId}
                onChange={(e) => props.onAuthorChange(e.target.value)}
                style={input}
              >
                {props.editors.map((ed) => (
                  <option key={ed.id} value={ed.id}>
                    {ed.name} ({ed.email})
                  </option>
                ))}
              </select>
            </Section>

            <Section label={t.settingsPanelSeries} hint={t.settingsPanelSeriesHint}>
              <select
                value={props.seriesId}
                onChange={(e) => props.onSeriesChange(e.target.value)}
                style={input}
              >
                <option value="">{t.settingsPanelSeriesNone}</option>
                {allSeries.map((s) => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </select>
              {props.seriesId && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 11, color: theme.text3, marginBottom: 4, display: "block" }}>
                    {t.settingsPanelSeriesPosition}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={props.seriesPosition || 1}
                    onChange={(e) => props.onSeriesPositionChange(Math.max(1, parseInt(e.target.value || "1", 10)))}
                    style={input}
                  />
                </div>
              )}
            </Section>

            <Section label={t.settingsPanelVisibility}>
              <ToggleRow
                checked={props.isPremium}
                onChange={props.onPremiumChange}
                label={t.settingsPanelPremium}
              />
              <ToggleRow
                checked={props.featured}
                onChange={props.onFeaturedChange}
                label={t.settingsPanelFeatured}
              />
            </Section>

            {props.article && (
              <Section label={t.settingsPanelMetadata}>
                <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.6 }}>
                  <div>{t.settingsPanelViews}: {props.article.view_count}</div>
                  <div>{t.settingsPanelCreated}: {new Date(props.article.created).toLocaleString()}</div>
                  <div>{t.settingsPanelUpdated}: {new Date(props.article.updated).toLocaleString()}</div>
                  {props.article.published_at && (
                    <div>{t.settingsPanelPublished}: {new Date(props.article.published_at).toLocaleString()}</div>
                  )}
                </div>
              </Section>
            )}
          </div>
        )}

        {tab === "audit" && (
          <div>
            {!props.article ? (
              <div style={{ color: theme.text3, fontSize: 12 }}>{t.settingsPanelSaveFirstHint}</div>
            ) : auditLoading ? (
              <div style={{ color: theme.text3, fontSize: 12 }}>{t.loading}</div>
            ) : audit.length === 0 ? (
              <div style={{ color: theme.text3, fontSize: 12 }}>{t.auditEmpty}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {audit.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      padding: 10,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      background: theme.bg,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>
                      {a.action}
                    </div>
                    <div style={{ fontSize: 11, color: theme.text3, marginTop: 4 }}>
                      {a.expand?.actor?.name || a.actor} ·{" "}
                      {new Date(a.created).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function Section({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          color: theme.text3,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: theme.text3, lineHeight: 1.4 }}>{hint}</span>}
    </div>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          width: 28,
          height: 16,
          borderRadius: 100,
          background: checked ? theme.accent : theme.borderStrong,
          position: "relative",
          transition: "background 0.15s",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 14 : 2,
            width: 12,
            height: 12,
            borderRadius: 100,
            background: "#fff",
            transition: "left 0.15s",
          }}
        />
      </span>
      <span style={{ fontSize: 12, color: theme.text }}>{label}</span>
    </button>
  );
}

function pill(active: boolean): React.CSSProperties {
  return {
    padding: "5px 10px",
    fontSize: 11,
    borderRadius: 100,
    background: active ? theme.accentSoft : theme.bg,
    color: active ? theme.accent : theme.text2,
    border: `1px solid ${active ? theme.accentStrong : theme.border}`,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}

const input: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  fontSize: 12,
  color: theme.text,
  outline: "none",
  fontFamily: theme.sans,
};

const ghostBtn: React.CSSProperties = {
  padding: "5px 10px",
  background: "transparent",
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  fontSize: 11,
  color: theme.text2,
  cursor: "pointer",
};

// ── TagsInput ──────────────────────────────────────────────────────────────
//
// Token-input для тегов:
//   • Выбранные теги — chip'ы со значком ×.
//   • Input ниже chip'ов: при наборе показывает dropdown matching existing.
//   • Enter:
//       - на matching tag из dropdown → выбрать его
//       - на полностью новом значении → POST в /api/collections/tags/records
//         → автоматически выбрать в этой статье
//   • Backspace на пустом input → удалить последний chip.
//   • Click на existing-tag в dropdown → toggle.
//   • Esc → закрыть dropdown.

function TagsInput({
  allTags,
  selectedIds,
  onChange,
  onTagsListUpdate,
}: {
  allTags: Tag[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onTagsListUpdate: (next: Tag[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedTags = useMemo(
    () => selectedIds.map((id) => allTags.find((t) => t.id === id)).filter((t): t is Tag => !!t),
    [allTags, selectedIds],
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Без запроса — показываем все НЕвыбранные (топ-12 для компактности).
      return allTags.filter((tg) => !selectedIds.includes(tg.id)).slice(0, 12);
    }
    return allTags
      .filter(
        (tg) =>
          tg.name.toLowerCase().includes(q) ||
          (tg.slug && tg.slug.toLowerCase().includes(q)),
      )
      .slice(0, 12);
  }, [query, allTags, selectedIds]);

  const exactMatch = useMemo(
    () => allTags.find((tg) => tg.name.toLowerCase() === query.trim().toLowerCase()),
    [allTags, query],
  );
  const canCreate = query.trim().length > 0 && !exactMatch;

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function addExisting(tag: Tag) {
    if (selectedIds.includes(tag.id)) return;
    onChange([...selectedIds, tag.id]);
    setQuery("");
    setHighlightIdx(0);
    inputRef.current?.focus();
  }

  function removeTag(id: string) {
    onChange(selectedIds.filter((x) => x !== id));
    inputRef.current?.focus();
  }

  async function createAndAdd() {
    const name = query.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const slug = slugifyForTag(name);
      const newTag = await createRecord<Tag>("tags", {
        name,
        slug,
        color: "#b8864a", // дефолт — bronze. Юзер может потом изменить в Tags page.
      });
      onTagsListUpdate([...allTags, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      onChange([...selectedIds, newTag.id]);
      setQuery("");
      setHighlightIdx(0);
      inputRef.current?.focus();
    } catch (e) {
      console.warn("create tag failed:", e);
    } finally {
      setCreating(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions.length > 0 && highlightIdx < suggestions.length) {
        addExisting(suggestions[highlightIdx]);
      } else if (canCreate) {
        void createAndAdd();
      }
    } else if (e.key === "Backspace" && query === "" && selectedTags.length > 0) {
      e.preventDefault();
      removeTag(selectedTags[selectedTags.length - 1].id);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
      setOpen(true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Box with chips + input */}
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          alignItems: "center",
          padding: 6,
          minHeight: 40,
          background: theme.bg,
          border: `1px solid ${open ? theme.accent : theme.border}`,
          borderRadius: 6,
          cursor: "text",
          transition: "border-color 0.12s",
        }}
      >
        {selectedTags.map((tg) => (
          <span
            key={tg.id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 4px 3px 9px",
              borderRadius: 100,
              fontSize: 11,
              background: theme.accentSoft,
              color: theme.accent,
              border: `1px solid ${theme.accentStrong}`,
            }}
          >
            {tg.color && (
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 100,
                  background: tg.color,
                  marginRight: 1,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
            )}
            {tg.name}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tg.id); }}
              title="Remove"
              style={{
                width: 16,
                height: 16,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                color: theme.accent,
                cursor: "pointer",
                borderRadius: 100,
                padding: 0,
                fontSize: 13,
                lineHeight: 1,
                fontFamily: "inherit",
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlightIdx(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? "Type to search or create…" : ""}
          style={{
            flex: 1,
            minWidth: 100,
            background: "transparent",
            border: "none",
            outline: "none",
            color: theme.text,
            fontSize: 12,
            fontFamily: "inherit",
            padding: "4px 4px",
          }}
        />
      </div>

      {/* Dropdown */}
      {open && (suggestions.length > 0 || canCreate) && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            maxHeight: 240,
            overflowY: "auto",
            zIndex: 20,
          }}
        >
          {suggestions.map((tg, i) => {
            const active = i === highlightIdx;
            const already = selectedIds.includes(tg.id);
            return (
              <button
                key={tg.id}
                type="button"
                onMouseEnter={() => setHighlightIdx(i)}
                onClick={() => addExisting(tg)}
                disabled={already}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "7px 10px",
                  background: active ? theme.accentSoft : "transparent",
                  border: "none",
                  color: already ? theme.text3 : theme.text,
                  fontSize: 12,
                  cursor: already ? "default" : "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                }}
              >
                {tg.color && (
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 100,
                      background: tg.color,
                      flexShrink: 0,
                    }}
                  />
                )}
                <span style={{ flex: 1 }}>{tg.name}</span>
                {already && <span style={{ fontSize: 10, color: theme.text3 }}>✓</span>}
              </button>
            );
          })}

          {canCreate && (
            <button
              type="button"
              onClick={() => void createAndAdd()}
              disabled={creating}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "7px 10px",
                background: suggestions.length === 0 || highlightIdx >= suggestions.length ? theme.accentSoft : "transparent",
                border: "none",
                borderTop: suggestions.length > 0 ? `1px solid ${theme.border}` : "none",
                color: theme.accent,
                fontSize: 12,
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
                fontWeight: 500,
              }}
            >
              <span>+</span>
              <span style={{ flex: 1 }}>
                {creating ? "Creating…" : `Create "${query.trim()}"`}
              </span>
              <kbd
                style={{
                  fontSize: 9,
                  padding: "1px 5px",
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 3,
                  color: theme.text3,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                ↵
              </kbd>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function slugifyForTag(s: string): string {
  const map: Record<string, string> = {
    а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh",
    з: "z", и: "i", й: "i", к: "k", л: "l", м: "m", н: "n", о: "o",
    п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "c",
    ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu",
    я: "ya",
  };
  return s
    .toLowerCase()
    .split("")
    .map((c) => map[c] ?? c)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}
